import React from "react";
import { Button } from "@/components/ui/button";
import {
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { formatCurrency } from "@/lib/format";

const STATUS_CONFIG = {
  ok: { color: "#00C97D", bg: "#D1FAE5", label: "En curso", Icon: CheckCircle2 },
  warning: { color: "#F59E0B", bg: "#FEF3C7", label: "Atención", Icon: AlertTriangle },
  exceeded: { color: "#EF4444", bg: "#FEE2E2", label: "Rebasado", Icon: AlertTriangle },
};

export default function BudgetRow({ item, currency, onEdit, onDelete }) {
  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.ok;
  const barPct = Math.min(item.pct, 100);
  return (
    <div
      className="rounded-xl border border-[#2A3447] bg-[#131C2E] p-5 fade-up"
      data-testid={`budget-row-${item.category}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: cfg.bg }}
          >
            <cfg.Icon className="w-5 h-5" style={{ color: cfg.color }} />
          </div>
          <div className="min-w-0">
            <p className="font-display text-base font-semibold text-[#F1F5F9] truncate">
              {item.category}
            </p>
            <p
              className="text-xs text-[#64748B]"
              data-testid={`budget-row-${item.category}-status`}
            >
              {cfg.label} · {item.pct}%
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-[#1B2638]"
            onClick={() => onEdit(item)}
            data-testid={`budget-edit-${item.category}`}
            aria-label={`Editar presupuesto de ${item.category}`}
          >
            <Pencil className="w-4 h-4 text-[#64748B]" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-[#FEE2E2]"
            onClick={() => onDelete(item)}
            data-testid={`budget-delete-${item.category}`}
            aria-label={`Eliminar presupuesto de ${item.category}`}
          >
            <Trash2 className="w-4 h-4 text-[#EF4444]" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="h-2.5 bg-[#1B2638] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${barPct}%`,
              backgroundColor: cfg.color,
            }}
            data-testid={`budget-progress-${item.category}`}
          />
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="font-numeric text-[#F1F5F9]">
            <span className="font-semibold" style={{ color: cfg.color }}>
              {formatCurrency(item.spent, currency)}
            </span>
            <span className="text-[#94A3B8]">
              {" "}
              / {formatCurrency(item.monthly_limit, currency)}
            </span>
          </span>
          <span className="font-numeric text-xs text-[#64748B]">
            {item.status === "exceeded"
              ? `+${formatCurrency(item.spent - item.monthly_limit, currency)} excedido`
              : `${formatCurrency(item.remaining, currency)} disponible`}
          </span>
        </div>
      </div>
    </div>
  );
}
